/**
 * SportPage — /sport/:slug
 *
 * Lists all scheduled + live events for a sport using the shared EventCard
 * (home/away labels, Home/Draw/Away odds, favourite ★, return hint).
 */
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import EventCard from '../components/EventCard';
import { getEvents, getEventDetail, type EventSummary, type EventDetail } from '../services/api';

const SPORT_ICONS: Record<string, string> = {
  football: '⚽', basketball: '🏀', baseball: '⚾', cricket: '🏏',
  tennis: '🎾', mma: '🥊', boxing: '🥊', 'ice-hockey': '🏒',
  'american-football': '🏈', rugby: '🏉', 'aussie-rules': '🏉',
};

export default function SportPage() {
  const { slug } = useParams<{ slug: string }>();
  const [events, setEvents] = useState<EventSummary[]>([]);
  const [details, setDetails] = useState<Record<string, EventDetail>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    if (!slug) return;
    setLoading(true);
    setError(false);
    try {
      const res = await getEvents(slug);
      const list: EventSummary[] = Array.isArray(res)
        ? res
        : (res as { data: EventSummary[] }).data ?? [];
      setEvents(list);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => { load(); }, [load]);

  // Fan out odds detail for the first 20 events.
  useEffect(() => {
    let active = true;
    events.slice(0, 20).forEach((ev) => {
      if (details[ev.id]) return;
      getEventDetail(ev.id)
        .then((d) => {
          const detail = (d as { data?: EventDetail }).data ?? (d as unknown as EventDetail);
          if (active) setDetails((prev) => ({ ...prev, [ev.id]: detail }));
        })
        .catch(() => {});
    });
    return () => { active = false; };
  }, [events]); // eslint-disable-line react-hooks/exhaustive-deps

  const title = slug ? slug.replace(/-/g, ' ') : 'Sport';
  const icon = slug ? SPORT_ICONS[slug] ?? '🏅' : '🏅';

  return (
    <div className="flex min-h-screen flex-col font-sans bg-white">
      <Header />
      <main className="flex-1 mx-auto w-full max-w-6xl px-4 py-10 sm:px-6 lg:px-8">
        <div className="mb-6 flex items-center gap-3">
          <span className="text-3xl">{icon}</span>
          <h1 className="font-heading text-2xl font-bold capitalize text-violet-900 sm:text-3xl">{title}</h1>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="space-y-3 rounded-card border border-border bg-white p-4 shadow-card">
                <div className="shimmer h-3 w-1/2 rounded" />
                <div className="shimmer h-4 w-3/4 rounded" />
                <div className="grid grid-cols-3 gap-2">
                  <div className="shimmer h-[52px] rounded-btn" />
                  <div className="shimmer h-[52px] rounded-btn" />
                  <div className="shimmer h-[52px] rounded-btn" />
                </div>
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="rounded-card border border-error/20 bg-error/5 p-6 text-center">
            <p className="mb-3 text-sm text-error">Couldn't load matches.</p>
            <button onClick={load} className="rounded-btn bg-primary px-4 py-2 text-sm font-semibold text-white">
              Try again
            </button>
          </div>
        ) : events.length === 0 ? (
          <div className="rounded-card border border-border bg-surface-2 p-10 text-center text-sm text-text-muted">
            No {title} matches available right now. Check back soon.
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {events.map((ev) => (
              <EventCard key={ev.id} event={ev} detail={details[ev.id]} />
            ))}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
